import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Printer, Stamp } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { AiInsightCard } from '../../components/ui/AiInsightCard';
import { HankoStamp } from '../../components/ui/HankoStamp';
import { api } from '../../lib/api';
import { formatAmount, formatDate } from '../../lib/format';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useCurrentUser } from '../../hooks/useCurrentUser';

interface TrialBalanceRow {
  account_id: string; code: string; name: string; kind: string;
  debit_cents: string; credit_cents: string;
}
interface ChecklistResp {
  period: { year: number; month: number; startDate: string; endDate: string };
  checklist: Array<{ key: string; label: string; count: number; ok: boolean }>;
}

/**
 * Monthly close packet — the auditor-ready PDF view that wraps up the
 * trial balance, the close checklist, and the close-out hanko approval
 * trail. Prints cleanly to A4 / Letter via the browser's print dialog.
 */
export function ClosePacketPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const { data: ws } = useWorkspace(wsId);
  const { data: user } = useCurrentUser();
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);

  const periodStart = new Date(Date.UTC(year, month - 1, 1)).toISOString().slice(0, 10);
  const periodEnd = new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);

  const checklist = useQuery<ChecklistResp>({
    queryKey: ['close-checklist', wsId, year, month],
    enabled: !!wsId,
    queryFn: async () =>
      (await api.get(`/workspaces/${wsId}/closing/checklist?year=${year}&month=${month}`)).data,
  });
  const tb = useQuery<TrialBalanceRow[]>({
    queryKey: ['close-packet-tb', wsId, periodStart, periodEnd],
    enabled: !!wsId,
    queryFn: async () =>
      (await api.get(`/workspaces/${wsId}/ledger/trial-balance?from=${periodStart}&to=${periodEnd}`)).data,
  });

  const currency = ws?.base_currency ?? 'JPY';
  const totalDebit = (tb.data ?? []).reduce((s, r) => s + Number(r.debit_cents), 0);
  const totalCredit = (tb.data ?? []).reduce((s, r) => s + Number(r.credit_cents), 0);

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-5xl print:px-0 print:py-0 print:max-w-none">
        <div className="flex items-center justify-between mb-4 print:hidden">
          <div className="flex items-center gap-3">
            <h1 className="display text-2xl font-semibold">{t('closePacket.title')}</h1>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 px-3 rounded-lg border border-border bg-card text-sm num"
            >
              {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="h-9 px-3 rounded-lg border border-border bg-card text-sm num"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-muted"
          >
            <Printer size={14} /> {t('closePacket.print')}
          </button>
        </div>

        <div className="mb-6 print:hidden">
          <AiInsightCard
            agent="close_coach"
            title={t('aiOutput.cards.closePacket')}
            prompt={t('aiOutput.prompts.closePacket', { year, month: String(month).padStart(2, '0') })}
          />
        </div>

        <Card className="print:border-0 print:shadow-none">
          <CardHeader className="text-center print:border-b print:border-border">
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              {ws?.name ?? '—'} · {t('closePacket.title')}
            </div>
            <div className="display text-3xl font-semibold mt-2">
              {t('closePacket.headerWithPeriod', { year, month: String(month).padStart(2, '0') })}
            </div>
            <div className="text-xs text-muted-foreground mt-1 num">
              {periodStart} → {periodEnd}
            </div>
          </CardHeader>

          <CardBody className="space-y-8">
            <section>
              <h2 className="font-medium mb-3">{t('closePacket.checklistHeading')}</h2>
              {checklist.isLoading ? (
                <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
              ) : (
                <ul className="divide-y divide-border border border-border rounded-lg">
                  {checklist.data?.checklist.map((item) => (
                    <li key={item.key} className="px-4 py-2.5 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-3">
                        <span
                          className={
                            'inline-block h-2 w-2 rounded-full ' +
                            (item.ok ? 'bg-emerald-500' : 'bg-warn-500')
                          }
                          aria-hidden
                        />
                        <span>{t(`closing.checklistItems.${item.key}`, item.label)}</span>
                      </div>
                      <span className="num">{item.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h2 className="font-medium mb-3">{t('closePacket.trialBalanceHeading')}</h2>
              {tb.isLoading ? (
                <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
              ) : (tb.data ?? []).length === 0 ? (
                <div className="text-sm text-muted-foreground">{t('reports.noEntries')}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-border rounded-lg">
                    <thead className="bg-muted/40">
                      <tr className="text-left text-xs uppercase text-muted-foreground">
                        <th className="px-4 py-2 font-medium">{t('reports.account')}</th>
                        <th className="px-4 py-2 font-medium text-right">{t('ledger.debit')}</th>
                        <th className="px-4 py-2 font-medium text-right">{t('ledger.credit')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tb.data?.map((r) => (
                        <tr key={r.account_id} className="border-t border-border">
                          <td className="px-4 py-2">
                            <div className="font-mono text-xs text-muted-foreground">{r.code}</div>
                            <div>{r.name}</div>
                          </td>
                          <td className="px-4 py-2 text-right num">{formatAmount(r.debit_cents, currency, i18n.language)}</td>
                          <td className="px-4 py-2 text-right num">{formatAmount(r.credit_cents, currency, i18n.language)}</td>
                        </tr>
                      ))}
                      <tr className="font-semibold bg-muted/40 border-t border-border">
                        <td className="px-4 py-2">{t('common.total')}</td>
                        <td className="px-4 py-2 text-right num">{formatAmount(totalDebit, currency, i18n.language)}</td>
                        <td className="px-4 py-2 text-right num">{formatAmount(totalCredit, currency, i18n.language)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section>
              <h2 className="font-medium mb-3 flex items-center gap-2">
                <Stamp size={14} /> {t('closePacket.signoffHeading')}
              </h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="border border-border rounded-lg p-5 text-center">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
                    {t('closePacket.preparedBy')}
                  </div>
                  <div className="flex items-center justify-center">
                    <HankoStamp text={user?.hankoSealText ?? user?.name?.[0] ?? 'K'} size={72} />
                  </div>
                  <div className="mt-3 text-sm font-medium">{user?.name ?? '—'}</div>
                  <div className="text-xs text-muted-foreground num">{formatDate(new Date(), i18n.language)}</div>
                </div>
                <div className="border border-border rounded-lg p-5 text-center">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
                    {t('closePacket.approvedBy')}
                  </div>
                  <div className="h-[72px] w-[72px] mx-auto rounded-full border border-dashed border-border" />
                  <div className="mt-3 text-sm text-muted-foreground">{t('closePacket.awaitingSignoff')}</div>
                </div>
              </div>
            </section>
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
